import { useState } from 'react';
import { useCart } from '../context/CartContext';
import { useNavigate } from 'react-router-dom';
import { orderService } from '../services/orderService';
import { OrderForm, OrderFormData } from '../components/OrderForm';

export const CartPage = () => {
  const { cart, removeFromCart, updateQuantity, clearCart, getTotal } = useCart();
  const [showOrderForm, setShowOrderForm] = useState(false);
  const navigate = useNavigate();

  const handleCheckout = async (formData: OrderFormData) => {
    if (cart.length === 0) return;

    try {
      // Créer la commande avec les informations du formulaire
      const order = await orderService.createOrder(
        cart.map(item => ({
          id: item.product.id,
          name: item.product.name,
          price: item.product.price,
          quantity: item.quantity
        })),
        formData,
        formData.paymentMethod
      );
      
      // Vider le panier
      clearCart();
      
      // Rediriger vers la page de confirmation
      navigate(`/order-confirmation/${order.id}`);
    } catch (error) {
      console.error('Erreur lors de la commande:', error);
      alert('Une erreur est survenue lors de la commande. Veuillez réessayer.');
    }
  };

  return (
    <div className="pt-16 min-h-screen bg-gradient-to-br from-orange-50 to-pink-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-8">Votre Panier</h1>
        
        {cart.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-xl mb-4">Votre panier est vide</p>
            <button
              onClick={() => navigate('/store')}
              className="px-6 py-3 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors"
            >
              Retour à la boutique
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="space-y-4">
                {cart.map((item) => (
                  <div key={item.product.id} className="bg-white p-6 rounded-xl shadow-sm">
                    <div className="flex items-center space-x-4">
                      <img 
                        src={item.product.image} 
                        alt={item.product.name}
                        className="w-24 h-24 object-cover rounded-lg"
                      />
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg">{item.product.name}</h3>
                        <p className="text-gray-600">{item.product.price}€</p>
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => updateQuantity(item.product.id, item.quantity - 1)}
                            className="px-3 py-1 border rounded hover:bg-gray-100"
                          >
                            -
                          </button>
                          <span className="w-8 text-center">{item.quantity}</span>
                          <button
                            onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                            className="px-3 py-1 border rounded hover:bg-gray-100"
                          >
                            +
                          </button>
                        </div>
                        <button
                          onClick={() => removeFromCart(item.product.id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          Supprimer
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="lg:col-span-1">
              <div className="bg-white p-6 rounded-xl shadow-sm">
                <h2 className="text-xl font-bold mb-4">Récapitulatif</h2>
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span>Sous-total</span>
                    <span>{getTotal().toFixed(2)}€</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Frais de livraison</span>
                    <span>Gratuit</span>
                  </div>
                  <div className="border-t pt-4">
                    <div className="flex justify-between font-bold text-lg">
                      <span>Total</span>
                      <span>{getTotal().toFixed(2)}€</span>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowOrderForm(true)}
                    disabled={cart.length === 0}
                    className={`w-full py-3 text-white rounded-lg transition-colors ${
                      cart.length === 0
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-pink-500 hover:bg-pink-600'
                    }`}
                  >
                    Passer la commande
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showOrderForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl p-8 max-w-2xl w-full">
              <h2 className="text-2xl font-bold mb-6">Informations de commande</h2>
              <OrderForm
                onSubmit={handleCheckout}
                onCancel={() => setShowOrderForm(false)}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}; 